// MQTT over WebSocket — the React equivalent of the Flutter mqtt_controller.dart.
//
// The Flutter app uses MqttServerClient over TCP 1883. A browser / Capacitor
// webview cannot open raw TCP, so this connects over WebSocket (mqtt.js). The
// broker must expose a WS listener; set REACT_APP_MQTT_WS_URL accordingly.
//
// Publish (command): topic `<uno>/device`, payload `[<uno>]<message>#<userId>`
// Subscribe (status): topic `<uno>/client`, status is the substring between ] and #

import mqtt from 'mqtt';
import { setCloudUnreachable } from '../offline/offlineMode';

const WS_URL =
  process.env.REACT_APP_MQTT_WS_URL || 'ws://smartifyindia.co.in:9001/mqtt';

let client = null;
const listeners = new Set(); // (topic, payloadString) => void
const subscribed = new Map(); // topic -> active subscriber count (ref-count)

// Consecutive failed connections before the cloud is declared unreachable.
// Three at `reconnectPeriod` 2 s ≈ 6 s, deliberately not one: a single dropped
// WebSocket is normal and recovers by itself.
const FAILURES_BEFORE_OFFLINE = 3;
let failedAttempts = 0;

export function connectMqtt() {
  if (client && client.connected) return client;
  if (client) return client; // connecting in progress

  const clientId = `react_client_${Date.now()}`;
  client = mqtt.connect(WS_URL, {
    clientId,
    keepalive: 60,
    clean: true,
    reconnectPeriod: 2000,
    connectTimeout: 8000,
  });

  client.on('connect', () => {
    // Re-subscribe everything after a reconnect.
    subscribed.forEach((_count, t) => client.subscribe(t));
    // Proof the cloud is reachable — the only thing that can clear this.
    setCloudUnreachable(false);
    failedAttempts = 0;
    // eslint-disable-next-line no-console
    console.log('[MQTT] connected', WS_URL);
  });
  client.on('message', (topic, message) => {
    const payload = message.toString();
    listeners.forEach((fn) => {
      try {
        fn(topic, payload);
      } catch (e) {
        /* ignore listener error */
      }
    });
  });
  client.on('error', (e) => console.error('[MQTT] error', e?.message || e));

  // ── Deciding the cloud is unreachable ────────────────────────────────────
  //
  // mqtt.js retries forever on `reconnectPeriod`, so a single failure proves
  // nothing — a WebSocket can drop on a handover and be back a second later,
  // and flipping the whole app into BLE mode for that would be worse than the
  // problem. Only a RUN of failures is evidence, and any successful connect
  // resets the count.
  //
  // At 2 s apart this declares the cloud down after roughly six seconds, which
  // is close to how long the firmware takes to reach the same conclusion and
  // start advertising over BLE.
  const onFailure = (why) => {
    failedAttempts += 1;
    if (failedAttempts >= FAILURES_BEFORE_OFFLINE) {
      // eslint-disable-next-line no-console
      console.warn(`[MQTT] unreachable after ${failedAttempts} attempts (${why}) — offline`);
      setCloudUnreachable(true);
    }
  };

  client.on('close', () => onFailure('close'));
  // 'offline' fires when mqtt.js gives up on the current socket.
  client.on('offline', () => onFailure('offline'));

  return client;
}

export function isConnected() {
  return !!client && client.connected;
}

// Resolve once the broker is actually connected, or false if it doesn't happen
// within `ms`.
//
// Anything that PUBLISHES AND EXPECTS AN ANSWER must await this first.
// subscribeTopic() defers its subscribe until the connection is up, while
// publishMessage() hands the message to mqtt.js's outgoing queue — so a
// publish issued before connect can reach the broker BEFORE the subscription
// does, and the reply is then delivered to nobody. That failure looks exactly
// like a dead device.
export function waitForConnect(ms = 6000) {
  if (isConnected()) return Promise.resolve(true);
  connectMqtt();
  return new Promise((resolve) => {
    let settled = false;
    const done = (v) => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      clearTimeout(timer);
      resolve(v);
    };
    // Polled rather than hung off a 'connect' listener: the client may already
    // be mid-handshake from an earlier call, whose event has been and gone.
    const poll = setInterval(() => { if (isConnected()) done(true); }, 100);
    const timer = setTimeout(() => done(false), ms);
  });
}

// Tear down the connection (e.g. on logout) so a new session starts fresh.
export function disconnectMqtt() {
  if (client) {
    try { client.end(true); } catch (e) { /* ignore */ }
    client = null;
  }
  subscribed.clear();
}

export function subscribeTopic(topic) {
  connectMqtt();
  const count = subscribed.get(topic) || 0;
  subscribed.set(topic, count + 1);
  // Only hit the broker on the first subscriber for this topic.
  if (count === 0 && client && client.connected) client.subscribe(topic);
}

export function unsubscribeTopic(topic) {
  const count = subscribed.get(topic) || 0;
  if (count <= 1) {
    subscribed.delete(topic);
    if (client && client.connected) client.unsubscribe(topic);
  } else {
    subscribed.set(topic, count - 1);
  }
}

export function publishMessage(topic, message) {
  connectMqtt();
  if (client) client.publish(topic, message, { qos: 1 });
}

// Subscribe to incoming messages. Returns an unsubscribe function.
export function addMessageListener(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Parse the status substring between ] and # from a `<uno>/client` payload.
export function parseStatus(payload) {
  if (typeof payload !== 'string') return '';
  const start = payload.indexOf(']');
  const end = payload.indexOf('#');
  if (start === -1) return payload;
  return payload.substring(start + 1, end === -1 ? undefined : end);
}
